import { ImagePlus, Images, Sparkles } from "lucide-react";
import {
	type KeyboardEvent,
	type ReactNode,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";

const VIEWPORT_MARGIN = 8;

function MenuItem({
	icon,
	label,
	onSelect,
}: {
	icon: ReactNode;
	label: string;
	onSelect: () => void;
}) {
	return (
		<button
			className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:bg-accent focus-visible:bg-accent [&_svg]:size-4 [&_svg]:text-muted-foreground"
			onClick={onSelect}
			role="menuitem"
			type="button"
		>
			{icon}
			{label}
		</button>
	);
}

/**
 * Right-click menu for empty canvas space. Positioned at the cursor (kept on
 * screen) and closed by Escape, a click elsewhere, or picking an item.
 */
export function CanvasContextMenu({
	x,
	y,
	onAddPrompt,
	onAddPluck,
	onUploadImage,
	onClose,
}: {
	onAddPluck: () => void;
	onAddPrompt: () => void;
	onClose: () => void;
	onUploadImage: () => void;
	x: number;
	y: number;
}) {
	const ref = useRef<HTMLDivElement>(null);
	const [position, setPosition] = useState({ left: x, top: y });

	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) {
			return;
		}
		const { width, height } = el.getBoundingClientRect();
		setPosition({
			left: Math.min(x, window.innerWidth - width - VIEWPORT_MARGIN),
			top: Math.min(y, window.innerHeight - height - VIEWPORT_MARGIN),
		});
		el.querySelector<HTMLButtonElement>("[role=menuitem]")?.focus();
	}, [x, y]);

	useEffect(() => {
		const onPointerDown = (event: PointerEvent) => {
			if (!ref.current?.contains(event.target as globalThis.Node)) {
				onClose();
			}
		};
		window.addEventListener("pointerdown", onPointerDown, true);
		window.addEventListener("blur", onClose);
		return () => {
			window.removeEventListener("pointerdown", onPointerDown, true);
			window.removeEventListener("blur", onClose);
		};
	}, [onClose]);

	const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key === "Escape") {
			onClose();
			return;
		}
		if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
			return;
		}
		event.preventDefault();
		const items = [
			...(ref.current?.querySelectorAll<HTMLButtonElement>("[role=menuitem]") ??
				[]),
		];
		const current = items.indexOf(document.activeElement as HTMLButtonElement);
		const step = event.key === "ArrowDown" ? 1 : -1;
		items.at((current + step) % items.length)?.focus();
	};

	const select = (action: () => void) => () => {
		onClose();
		action();
	};

	return (
		<div
			className="fixed z-50 min-w-44 rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
			onContextMenu={(event) => event.preventDefault()}
			onKeyDown={onKeyDown}
			ref={ref}
			role="menu"
			style={position}
		>
			<MenuItem
				icon={<Sparkles />}
				label="Add prompt"
				onSelect={select(onAddPrompt)}
			/>
			<MenuItem
				icon={<Images />}
				label="Add pluck…"
				onSelect={select(onAddPluck)}
			/>
			<MenuItem
				icon={<ImagePlus />}
				label="Upload image…"
				onSelect={select(onUploadImage)}
			/>
		</div>
	);
}
